import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { pipeLatLngs } from './model';
import { materialRows } from './materialList';
import { SPRAY_DEF } from './spray';
import { money, locale } from './units';
import { clean, download, safeName } from './exporters';
import { APP_INFO } from './license';
import { companyLine } from './company';

const VALVES = ['g04', 'g05', 'g32', 'g33', 'g34', 'g35', 'g36', 'g37', 'g38', 'g39', 'g40', 'g41'];
const CONN_PRICE = { tee: 4.5, cross: 6, elbow: 3.5, coupling: 3, reducer: 3.8, cap: 2, sprConn: 6.5, thread: 3 };
const connPrice = (kind, dn) => { const d = parseFloat(String(dn).split('/')[0]) || 25; return Math.round(CONN_PRICE[kind] * Math.max(0.7, 1 + (d - 25) / 40) * 100) / 100; };
const isTee = (f) => /^(tee|cross)/.test(f?.type || '');
const dOf = (dn) => (dn ? `d${dn}` : '');
const bearing = (a, b) => Math.atan2((b[1] - a[1]) * Math.cos((a[0] * Math.PI) / 180), b[0] - a[0]);

// connectors derived from the drawing: tees at branches, elbows at sharp bends, couplings / reducers at joints, end caps, sprinkler connections
export function irrConnectors(project) {
  const fits = project.fittings || [], byId = Object.fromEntries(fits.map((f) => [f.id, f]));
  const nodes = {}, out = {};
  const add = (k, dn, n = 1) => { const key = `${k}|${dn || ''}`; out[key] = (out[key] || 0) + n; };
  const key = (p) => (p?.fid ? `f:${p.fid}` : Array.isArray(p) ? `c:${p[0].toFixed(6)},${p[1].toFixed(6)}` : null);
  (project.pipes || []).forEach((pp) => {
    const P = pipeLatLngs(pp, fits), raw = pp.points || [];
    raw.forEach((p, i) => {
      const k = key(p); if (!k) return;
      const n = nodes[k] || (nodes[k] = { fid: p.fid, ends: [], pass: [] });
      if (i === 0 || i === raw.length - 1) n.ends.push(pp.dn || '');
      else if (p.fid) n.pass.push(pp.dn || '');
      else if (P[i - 1] && P[i + 1]) {
        let d = Math.abs(bearing(P[i - 1], P[i]) - bearing(P[i], P[i + 1])) * 180 / Math.PI;
        if (d > 180) d = 360 - d;
        if (d > 45) add('elbow', pp.dn);
      }
    });
  });
  Object.values(nodes).forEach((n) => {
    const f = n.fid && byId[n.fid], all = [...n.ends, ...n.pass];
    if (f && isTee(f)) return add(f.type.startsWith('cross') ? 'cross' : 'tee', f.hostDn || all[0]);
    if (f && SPRAY_DEF[f.catId] != null) { const c = n.ends.length + 2 * n.pass.length; if (c > 1) add('tee', all[0], c - 1); if (c) add('sprConn', all[0]); return; }
    if (f) { n.pass.forEach((dn) => add('tee', dn)); n.ends.forEach((dn) => add('thread', dn)); return; }
    if (n.ends.length === 1) return add('cap', n.ends[0]);
    if (n.ends.length === 2) return add(n.ends[0] === n.ends[1] ? 'coupling' : 'reducer', n.ends[0] === n.ends[1] ? n.ends[0] : `${n.ends[0]}/${n.ends[1]}`);
    if (n.ends.length > 2) add('tee', n.ends[0], n.ends.length - 2);
  });
  return Object.entries(out).map(([k, qty]) => { const [kind, dn] = k.split('|'); return { kind, dn, qty }; });
}

// grouped bill of materials of the drawn irrigation system
export function irrBom(project, t) {
  const { rows } = materialRows(project);
  const fits = project.fittings || [];
  const grp = { pipes: [], spr: [], valves: [], other: [], conn: [] };
  rows.forEach((r) => {
    const id = r.key.split('|')[0], row = { ...r, dim: r.dim.replace(/^DN /, 'd') };
    row.label = [row.name, row.dim].filter(Boolean).join(' · ');
    if (r.unit === 'lfm') grp.pipes.push(row);
    else if (SPRAY_DEF[id] != null || id === 'g31') grp.spr.push(row);
    else if (VALVES.includes(id) || fits.some((f) => f.catId === id && f.type === 'valve')) grp.valves.push(row);
    else if (id === 'g42') grp.conn.push(row);
    else grp.other.push(row);
  });
  irrConnectors(project).forEach((c) => grp.conn.push({ key: `auto:${c.kind}:${c.dn}`, label: [t['bom_' + c.kind], dOf(c.dn).replace('/', '/d')].filter(Boolean).join(' · '), unit: 'pcs', qty: c.qty, price: connPrice(c.kind, c.dn), amount: connPrice(c.kind, c.dn) * c.qty, auto: true }));
  grp.pipes.sort((a, b) => (parseFloat(a.dim.slice(1)) || 0) - (parseFloat(b.dim.slice(1)) || 0));
  const total = Object.values(grp).flat().reduce((a, r) => a + (r.amount || 0), 0);
  return { grp, total };
}

export const BOM_SECS = ['pipes', 'spr', 'valves', 'conn', 'other'];
export const bomQty = (r, t) => (r.unit === 'lfm' ? `${r.qty.toLocaleString(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m` : `${Math.round(r.qty * 100) / 100} ${t.unit_pcs}`);

export function irrBomXLSX(project, t) {
  const { grp, total } = irrBom(project, t);
  const aoa = [[`${t.bomTitle} – ${project.title}`], [companyLine()], [], [t.bomPos, t.rmQty, t.mfColUnit, t.lvEp, t.lvGp]];
  BOM_SECS.filter((s) => grp[s].length).forEach((s) => {
    aoa.push([t['bomSec_' + s]]);
    grp[s].forEach((r) => aoa.push([r.label + (r.auto ? ` (${t.bomAuto})` : ''), Math.round(r.qty * 100) / 100, r.unit === 'lfm' ? 'm' : t.unit_pcs, r.price ?? '', r.amount != null ? Math.round(r.amount * 100) / 100 : '']));
    aoa.push([]);
  });
  aoa.push([t.vatNet, '', '', '', Math.round(total * 100) / 100]);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [50, 10, 8, 12, 14].map((wch) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, t.bomSheet);
  download(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${safeName(project.title)}_Bewaesserung_Stueckliste.xlsx`);
}

export function irrBomPdf(project, t) {
  const { grp, total } = irrBom(project, t), doc = new jsPDF({ unit: 'mm', format: 'a4' }), M = 14;
  let y = 0;
  const head = () => {
    doc.setFillColor(21, 128, 61); doc.rect(0, 0, 210, 22, 'F'); doc.setTextColor(255);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text(clean(`${t.bomTitle} – ${project.title}`), M, 10);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.text(clean([companyLine(), new Date().toLocaleDateString(locale())].filter(Boolean).join(' · ')), M, 17);
    doc.setTextColor(20); y = 30;
  };
  const ensure = (h) => { if (y + h > 282) { doc.addPage(); head(); } };
  head();
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.text(clean(t.bomPos), M + 2, y); doc.text(clean(t.rmQty), 150, y, { align: 'right' }); doc.text(clean(t.lvEp), 172, y, { align: 'right' }); doc.text(clean(t.lvGp), 196, y, { align: 'right' });
  doc.setDrawColor(148, 163, 184); doc.line(M, y + 1.5, 196, y + 1.5); y += 7;
  BOM_SECS.filter((s) => grp[s].length).forEach((s) => {
    ensure(14); doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.text(clean(t['bomSec_' + s]), M, y); y += 5.5;
    doc.setFontSize(9);
    grp[s].forEach((r) => {
      const ln = doc.splitTextToSize(clean(r.label + (r.auto ? ` (${t.bomAuto})` : '')), 110);
      ensure(ln.length * 4.2 + 2); doc.setFont('helvetica', 'normal');
      doc.text(ln, M + 2, y); doc.text(clean(bomQty(r, t)), 150, y, { align: 'right' });
      if (r.price != null) doc.text(clean(money(+r.price)), 172, y, { align: 'right' });
      if (r.amount != null) doc.text(clean(money(r.amount)), 196, y, { align: 'right' });
      y += ln.length * 4.2 + 1.2;
    });
    y += 3;
  });
  ensure(10); doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.text(clean(`${t.vatNet}: ${money(total)}`), 196, y + 2, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(120); doc.text(clean(`${t.bomAutoHint} · ${APP_INFO.name}`), M, 290);
  doc.save(`${safeName(project.title)}_Bewaesserung_Stueckliste.pdf`);
}
